import { Component, Prop, Vue } from 'vue-property-decorator';
import { AvatarSelectionOptions, SelectionOptions, UserDTO, UserEngagementDTO } from '@/models';
import { buildUserEngagementRoleOptions, isValidNumericInputKey } from '@/helpers';
import { getUserDisplayInfo } from '@/services';
import PopupMenu from '@/components/common/popup-menu/PopupMenu.vue';
import UserInformation from '@/components/common/user-information/UserInformation.vue';

@Component({
  components: { PopupMenu, UserInformation }
})
export default class ProjectTeamMemberForm extends Vue {
  private formElementProps = {
    outlined: true,
    dense: true,
    required: true
  };

  $refs!: {
    menuRef: HTMLFormElement;
    formRef: HTMLFormElement;
  };

  @Prop({ default: () => [] }) members: UserEngagementDTO[];
  @Prop({ default: false }) isEditing: boolean;

  private member = { user: { id: -1 } } as UserEngagementDTO;
  private editedUserId = -1;
  private userOptions = [] as AvatarSelectionOptions;
  private roleOptions = [] as SelectionOptions;
  private ruleOptions = {
    name: [
      (v: number) => (!!v && v >= 1) || 'Name is required',
      (v: number) => (v && !this.checkUserExistInTeam(v)) || 'Employee already in team'
    ],
    role: [(v: string) => !!v || 'Role is required'],
    contribution: [(v: number) => !!v || 'Contribution is required', (v: number) => (v && v > 0) || 'Min: > 0']
  };

  //#region computed
  get users(): UserDTO[] {
    return this.$store.getters['user/users'];
  }

  //#endregion

  //#region build
  buildUserOptions(): AvatarSelectionOptions {
    return this.users
      .filter(user => user.id && user.code)
      .map(user => ({
        avatarUrl: user.avatarUrl,
        code: user.code,
        fullName: user.fullName,
        text: getUserDisplayInfo(user),
        value: user.id
      }));
  }

  //#endregion

  //#region get-data
  getTeamMemberIds(): number[] {
    const teamMemberIds = this.members.map(member => member.user.id);
    if (this.isEditing) {
      return teamMemberIds.filter(memberId => memberId !== this.member.user.id);
    }
    return teamMemberIds;
  }

  getUserInformation(userId: number): UserDTO {
    return this.users.find(user => user.id === userId) || ({} as UserDTO);
  }

  getUserEngagement(): UserEngagementDTO {
    return {
      ...this.member,
      user: { ...this.getUserInformation(this.editedUserId) },
      contribution: +this.member.contribution,
      individualDrops: undefined
    };
  }

  //#endregion

  onKeyDown(event: KeyboardEvent): void {
    if (!isValidNumericInputKey(event)) {
      event.preventDefault();
    }
  }

  checkUserExistInTeam(userId: number): number | undefined {
    return this.getTeamMemberIds().find(memberId => memberId === userId);
  }

  formValidate(): boolean {
    return this.$refs.formRef.validate();
  }

  onOk(): void {
    if (this.formValidate()) {
      this.$emit('onOk', this.getUserEngagement());
      this.$refs.formRef.reset();
      this.onCancel();
    }
  }

  onCancel(): void {
    this.$refs.formRef.reset();
    this.$refs.menuRef.closeMenu();
  }

  created(): void {
    this.userOptions = this.buildUserOptions();
    this.roleOptions = buildUserEngagementRoleOptions();
  }

  openMenu(memberToEdit: UserEngagementDTO): void {
    this.member = memberToEdit;
    this.editedUserId = this.member.user.id;
    this.$refs.menuRef.openMenu();
  }
}
